import { isoDateTime, z } from "../zod.js";
import { Locale } from "../locales.js";

// Module 17 — Messaging domain events (spec 17 §6): the typed catalog of what
// this module EMITS in slice P3-B part 1, plus the one event it CONSUMES.
//
// ELEVEN REGISTERED EVENTS — this slice's emitters and NO OTHERS (the D-135
// rule every module catalog restates: a name joins WITH its emitter, in the
// same change). The §6 row deliberately ABSENT:
//   * `messaging.template_status_changed` — its emitters are the §3.5 Meta
//     template lifecycle (submit route, status webhook, nightly reconcile),
//     ALL parked with WhatsApp (D-232): no BSP, no submission, no Meta
//     webhook. The variant-body PUT is an authoring edit, not a lifecycle
//     transition, and does not emit. The name lands at unparking with the
//     submit/sync surface.
//
// CONSUMERS, honestly. Module 12 is absent until P4 (charter stated
// absences) and modules 13/14 are unbuilt — every §6 consumer cell naming
// them is recorded on the payload doc block, not in the `consumers` array
// (D-175: a declared subscription nothing implements fails the live-registry
// parity gate in exactly the direction it exists for). The TWO live
// subscriptions this slice lands are module 09's `contact_channels.is_verified`
// mirror (spec 09 §6 consumed list): `identity_verified` and
// `identity_revoked` → consumers: ["09"]. P3-E PR-E1 (D-291 ruling 10) adds
// module 09's §9.8 delivery rollup onto its §2.20 notification rows:
// `message_failed` and `message_delivery_updated` → consumers: ["09"], moved
// with packages/contracts/src/events/sales.ts's consumed list (2 → 4) and the
// live-registry pin in outbox-registry-parity.int.test.ts (19 → 21). FOUR
// names carry ["09"] now.
//
// CONSUMED: `sales.customer_contact_deleted` (09 → 17 privacy cleanup — the
// payload carries the removed channel_ids so this module never reads back
// rows 09 already hard-deleted). The other §6 subscriptions
// (`sales.order_status_changed` — /status card cache, deferred to P3-D with
// /status itself; `sales.contact_channel_verified` — 09 has no emitter, its
// trigger IS this module's flow; `approval.requested`/`approval.decided` —
// D-231: nothing emits either in P3) are stated here and NOT declared.
//
// §6's shared envelope (tenant_id, event_id, occurred_at, actor) is added by
// `OutboxService.append`; each payload below carries only its own fields.
// Every payload is `.parse()`d at its emit site in
// `modules/messaging/messaging-events.ts`, and `messagingProducerEventNames`
// is held against that emitter's string literals BOTH WAYS by
// `test/unit/module-catalog-parity.test.ts`.

const Uuid = z.guid();
/** The shared provider enum. `email` joined with P3-E2 PR E2-2e (D-327
 * ruling 14): `message_received`, `identity_verified`, `identity_revoked` and
 * `provider_health_changed` can carry it. No event was added for email, and
 * `inbound_intake_created` and `conversation_opened` fire for an email
 * thread as for a chat one, with no provider field added to either. */
const Provider = z.enum(["telegram", "whatsapp", "email"]);
/** The providers a send can go out through. Nothing is ever sent by email
 * (D-327 ruling 9, owner question 10), so `message_sent` and
 * `message_failed` keep the two chat providers and an emit naming `email`
 * fails its parse at the emit site. */
const SendProvider = z.enum(["telegram", "whatsapp"]);
const OwnerKind = z.enum(["customer_contact", "user"]);
/** An email identity's normalized addr-spec (D-327 ruling 14), the key
 * module 09's mirror flips exactly one `contact_channels` row by. Present
 * exactly when `provider` is `email`: a chat edge never carries it, and an
 * email edge without it would leave the mirror unable to tell which of a
 * contact's addresses was verified, so either shape fails its parse at the
 * emit site. The mirror's own audit row keeps carrying no address (D-307
 * ruling 9). */
const IdentityAddress = z.string().min(3).max(320);

function addressMatchesProvider(
  v: { provider: z.infer<typeof Provider>; address?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (v.provider === "email" && v.address === undefined) {
    ctx.addIssue({
      code: "custom",
      path: ["address"],
      message: "an email identity edge carries its address (D-327 ruling 14)",
    });
  }
  if (v.provider !== "email" && v.address !== undefined) {
    ctx.addIssue({
      code: "custom",
      path: ["address"],
      message: "only an email identity edge carries an address (D-327 ruling 14)",
    });
  }
}

/** spec 17 §6 — `messaging.message_received`. §6 routing: 13 (unbuilt), 09
 * agent-flow trigger + quote-follow-up stop (both land with the slices that
 * own quotes/agent flows), 12 SLA timers (P4). Emitter: the webhook parse
 * worker on every inbound message row (§3.4.3), and the email worker on
 * every landed or released mail, with `provider` `email` and `content_kind`
 * `email` (§3.4's email arm, step 14). A held mail emits nothing until it is
 * released. */
export const MessagingMessageReceivedPayload = z.object({
  message_id: Uuid,
  conversation_id: Uuid,
  channel_identity_id: Uuid,
  provider: Provider,
  content_kind: z.string(),
  customer_id: Uuid.nullable(),
  has_media: z.boolean(),
});

/** spec 17 §6 — `messaging.inbound_intake_created`. §6 routing: 09 sales
 * intake queue + 14 offer agent. Module 14's offer agent consumes it since
 * P3-K K-2 (D-515 ruling 1); the 09 intake queue surface lands later (P5). Emitter: §3.4.4, exactly once per
 * customer inbound text/media message that is not a command/callback, and
 * once per email that lands or is released on a customer thread, whose
 * `body_preview` is taken from the body and never from the quoted part
 * (D-327 ruling 12). */
export const MessagingInboundIntakeCreatedPayload = z.object({
  message_id: Uuid,
  conversation_id: Uuid,
  customer_id: Uuid.nullable(),
  /** First 280 chars of the body (§6). */
  body_preview: z.string().max(280),
  media_count: z.number().int().min(0),
  language: Locale,
});

/** spec 17 §6 — `messaging.message_sent`. §6 routing: 12 fulfilment (P4),
 * 13 (unbuilt). Emitter: the send worker on provider accept (§3.3.5). */
export const MessagingMessageSentPayload = z.object({
  message_id: Uuid,
  conversation_id: Uuid,
  provider: SendProvider,
  template_code: z.string().nullable(),
  notification_id: Uuid.nullable(),
  sender_kind: z.enum(["contact", "user", "agent", "system"]),
  sender_id: Uuid.nullable(),
});

/** spec 17 §6 — `messaging.message_delivery_updated`. §6 routing: 09 §2.20
 * delivery rollup — LIVE since P3-E PR-E1 (D-291 ruling 10; module 09 finds
 * its row by `outbound_message_id` = `message_id` and advances `sent` →
 * `delivered`), 12 SLA (P4), 13. Emitter: the status-webhook path on a monotonic rollup advance
 * (§3.3.6; Telegram is sent-terminal — the stub adapter is what drives
 * delivered/read in tests, D-232/§10 preamble). */
export const MessagingMessageDeliveryUpdatedPayload = z.object({
  message_id: Uuid,
  status: z.enum(["delivered", "read"]),
  provider_ts: isoDateTime({ offset: true }).nullable(),
});

/** spec 17 §6 — `messaging.message_failed`. §6 routing: 09 §2.20 delivery
 * rollup — LIVE since P3-E PR-E1 (D-291 ruling 10; module 09 flips the row
 * `notification_id` names to `failed`), 12 email fallback + exception (P4 —
 * the §4.4 health surfaces stand in), 13. Emitter: the send
 * worker on retry exhaustion / permanent error, and the §3.3.2 policy gates
 * on suppression. */
export const MessagingMessageFailedPayload = z.object({
  message_id: Uuid,
  provider: SendProvider,
  error_class: z.enum(["retryable_exhausted", "permanent", "suppressed"]),
  status_detail: z.string().nullable(),
  notification_id: Uuid.nullable(),
  owner_kind: OwnerKind,
  owner_id: Uuid,
});

/** spec 17 §6 — `messaging.identity_verified`. §6 routing: 09 contact card
 * refresh + `contact_channels.is_verified` sync — LIVE this slice (spec 09
 * §6 consumed list; the 09-side consumer lands with this slice's seam) — and
 * 12 channel-availability (P4). Emitter: the §3.2.4 claim transaction, and
 * since P3-E2 PR E2-2e the email worker's own verification of a sender
 * module 09 holds and a release that verifies one (§3.4's email arm, steps 7
 * and 8), each carrying the verified `address`. */
export const MessagingIdentityVerifiedPayload = z
  .object({
    identity_id: Uuid,
    owner_kind: OwnerKind,
    owner_id: Uuid,
    provider: Provider,
    address: IdentityAddress.optional(),
  })
  .superRefine(addressMatchesProvider);

/** spec 17 §6 — `messaging.identity_revoked`. §6 routing: 09 contact channel
 * sync — LIVE this slice, the other half of the is_verified mirror — and 12
 * routing drop (P4). Emitter: the revoke route, the re-link claim's atomic
 * revoke, and the `sales.customer_contact_deleted` consumer. */
export const MessagingIdentityRevokedPayload = z
  .object({
    identity_id: Uuid,
    owner_kind: OwnerKind,
    owner_id: Uuid,
    provider: Provider,
    reason: z.string(),
    address: IdentityAddress.optional(),
  })
  .superRefine(addressMatchesProvider);

/** spec 17 §6 — `messaging.identity_opted_out`. §6 routing: 12 suppression
 * list (P4 — §3.3.2's own gates suppress in P3), 13. Emitter: the /stop and
 * STOP command handlers (§3.7, §9.15). */
export const MessagingIdentityOptedOutPayload = z.object({
  identity_id: Uuid,
  owner_kind: OwnerKind,
  owner_id: Uuid,
  hard: z.boolean(),
});

/** spec 17 §6 — `messaging.template_status_changed` is ABSENT — see the
 * header: its §3.5 emitters are parked with WhatsApp (D-232). */

/** spec 17 §6 — `messaging.callback_action_performed`. §6 routing: 12
 * confirmation trail (P4, as is its approval producer — charter stated
 * absences), 13. Emitters: the callback consume path for `set_language` (the
 * /start and /lang pickers — the §2.11 enum widening P3-B recorded) and, since
 * PR-E1 (D-291 ruling 13), `order_status_refresh` (the /status picker's
 * numbered buttons); approval/quote actions gain producers with their slices. */
export const MessagingCallbackActionPerformedPayload = z.object({
  callback_id: Uuid,
  action: z.string(),
  target_kind: z.string(),
  target_id: Uuid,
  identity_id: Uuid,
  result: z.string().nullable(),
});

/** spec 17 §6 — `messaging.conversation_opened`. §6 routing: 09 triage inbox
 * + Customer 360 thread surfacing (lands with 09's consuming surface), 13.
 * Emitter: thread creation — link claim (`link`), unknown-address inbound
 * (`inbound`); `manual` joins with part 2's claim screen. */
export const MessagingConversationOpenedPayload = z.object({
  conversation_id: Uuid,
  kind: z.enum(["customer", "staff"]),
  customer_id: Uuid.nullable(),
  source: z.enum(["inbound", "link", "manual"]),
});

/** spec 17 §6 — `messaging.provider_health_changed`. §6 routing: 12
 * exception when down > 30 min (P4 — the §4.4 banner is the P3 surface),
 * admin console SSE (part 2). Emitter: the send worker's §3.6 circuit
 * transitions, and for an `email` account the tenant's probe and its
 * landings when the receiving rule set's state changes (§2.13, D-327 ruling
 * 19), with `window_error_rate` 0 because nothing is sent. */
export const MessagingProviderHealthChangedPayload = z.object({
  channel_account_id: Uuid,
  provider: Provider,
  old_state: z.enum(["healthy", "degraded", "down"]),
  new_state: z.enum(["healthy", "degraded", "down"]),
  /** numeric(5,4) as exact decimal text (D-041). */
  window_error_rate: z.string().regex(/^(0(\.\d{1,4})?|1(\.0{1,4})?)$/),
});

export const messagingProducerEvents = {
  "messaging.message_received": {
    payload: MessagingMessageReceivedPayload,
    consumers: [],
  },
  "messaging.inbound_intake_created": {
    payload: MessagingInboundIntakeCreatedPayload,
    consumers: ["14"],
  },
  "messaging.message_sent": {
    payload: MessagingMessageSentPayload,
    consumers: [],
  },
  // P3-E PR-E1 (D-291 ruling 10): module 09's §9.8 delivery rollup.
  "messaging.message_delivery_updated": {
    payload: MessagingMessageDeliveryUpdatedPayload,
    consumers: ["09"],
  },
  // P3-E PR-E1 (D-291 ruling 10): module 09's §9.8 delivery rollup.
  "messaging.message_failed": {
    payload: MessagingMessageFailedPayload,
    consumers: ["09"],
  },
  "messaging.identity_verified": {
    payload: MessagingIdentityVerifiedPayload,
    consumers: ["09"],
  },
  "messaging.identity_revoked": {
    payload: MessagingIdentityRevokedPayload,
    consumers: ["09"],
  },
  "messaging.identity_opted_out": {
    payload: MessagingIdentityOptedOutPayload,
    consumers: [],
  },
  "messaging.callback_action_performed": {
    payload: MessagingCallbackActionPerformedPayload,
    consumers: [],
  },
  "messaging.conversation_opened": {
    payload: MessagingConversationOpenedPayload,
    consumers: [],
  },
  "messaging.provider_health_changed": {
    payload: MessagingProviderHealthChangedPayload,
    consumers: [],
  },
} as const;

export type MessagingEventName = keyof typeof messagingProducerEvents;

export const messagingProducerEventNames = Object.keys(
  messagingProducerEvents,
) as MessagingEventName[];

/**
 * Events module 17 CONSUMES over the relay (spec 17 §6 "Consumed by this
 * module"): exactly ONE in this slice — the module-09 seam. The subscriber
 * (modules/messaging/seams/customer-contact-deleted.consumer.ts) revokes
 * every identity bound to the archived contact off the payload's channel_ids
 * + owner id, emitting `messaging.identity_revoked` for each (reason
 * `owner_deleted`), deduped through messaging_processed_events. The three §6
 * names NOT declared are stated in the header with their reasons (D-175 —
 * the module-07 rule, verbatim).
 */
export const messagingConsumedEventNames = ["sales.customer_contact_deleted"] as const;
